<div class="relative inline-block text-left select-none">
  <!-- Trigger Button -->
  <div #triggerWrapper class="inline-block">
    <app-button
      [variant]="buttonVariant()"
      [size]="buttonSize()"
      [label]="buttonLabel()"
      [disabled]="disabled()"
      (btnClick)="toggleOpen($event)"
    >
      <svg left-icon width="16" height="16" viewBox="0 0 512 512" fill="currentColor">
        <path
          d="M448 96l0 128-160 0 0-128 160 0zm0 192l0 128-160 0 0-128 160 0zM224 224L64 224 64 96l160 0 0 128zM64 288l160 0 0 128L64 416l0-128zM64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32z"
        ></path>
      </svg>
    </app-button>
  </div>

  <!-- Customize Popover Panel -->
  @if (isOpen()) {
    <div
      flipPosition
      [flipPositionTrigger]="triggerElement()"
      [flipPositionAlign]="align()"
      class="absolute z-50 w-[290px] rounded-2xl bg-zinc-900 border border-zinc-800 p-4 shadow-2xl shadow-black/80 flex flex-col gap-4 text-text-primary"
      (click)="$event.stopPropagation()"
    >
      <!-- Section 1: VIEW -->
      <div class="flex flex-col gap-1.5 relative">
        <span class="text-[11px] font-bold tracking-wider text-text-dimmed uppercase">VIEW</span>
        <div
          #viewSelector
          class="flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-800/90 text-sm font-medium text-text-primary select-none cursor-pointer hover:border-zinc-700"
          (click)="toggleViewDropdown($event)"
        >
          <span class="truncate">{{ activeView()?.name ?? 'Default layout' }}</span>
          <svg
            class="w-4 h-4 text-text-dimmed shrink-0 transition-transform"
            [class.rotate-180]="isViewDropdownOpen()"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </div>

        @if (isViewDropdownOpen()) {
          <div
            flipPosition
            [flipPositionTrigger]="viewSelectorEl()?.nativeElement ?? null"
            class="absolute z-50 w-full rounded-lg bg-zinc-950 border border-zinc-800 shadow-2xl shadow-black/80 py-1 max-h-56 overflow-y-auto"
            (click)="$event.stopPropagation()"
          >
            <button
              type="button"
              (click)="selectDefaultLayout()"
              class="w-full text-left px-3 py-2 text-sm cursor-pointer hover:bg-zinc-800/60 transition-colors"
              [class.text-white]="!activeView()"
              [class.font-semibold]="!activeView()"
              [class.text-zinc-400]="!!activeView()"
            >
              Default layout
            </button>

            @for (view of views(); track view.id) {
              <div class="flex flex-wrap items-center gap-0.5 px-1.5 py-1 group hover:bg-zinc-800/60 rounded-md">
                @if (renamingViewId() === view.id) {
                  <input
                    #renameInput
                    type="text"
                    [value]="renameDraft()"
                    (input)="onRenameInput($any($event.target).value)"
                    (keydown.enter)="confirmRename()"
                    (keydown.escape)="cancelRename(); $event.stopPropagation()"
                    (click)="$event.stopPropagation()"
                    class="flex-1 min-w-0 h-7 px-2 text-sm bg-zinc-900 border rounded-md text-text-primary outline-none"
                    [class.border-zinc-700]="!renameError()"
                    [class.focus:border-indigo-500]="!renameError()"
                    [class.border-red-500]="!!renameError()"
                  />
                  <button
                    type="button"
                    (click)="confirmRename(); $event.stopPropagation()"
                    class="shrink-0 text-[10px] font-bold text-zinc-300 hover:text-white px-2 py-1 cursor-pointer"
                  >
                    Done
                  </button>
                  @if (renameError()) {
                    <p class="w-full px-1 pt-1 text-[11px] text-red-400">{{ renameError() }}</p>
                  }
                } @else {
                  <button
                    type="button"
                    (click)="selectSavedView(view)"
                    class="flex-1 min-w-0 text-left px-2 py-1.5 text-sm truncate cursor-pointer"
                    [class.text-white]="activeView()?.id === view.id"
                    [class.font-semibold]="activeView()?.id === view.id"
                    [class.text-zinc-400]="activeView()?.id !== view.id"
                  >
                    {{ view.name }}
                  </button>
                  <button
                    type="button"
                    (click)="startRename(view, $event)"
                    class="shrink-0 opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-white p-1.5 rounded-md cursor-pointer transition-opacity"
                    title="Rename view"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    (click)="copyViewLink(view, $event)"
                    class="shrink-0 p-1.5 rounded-md cursor-pointer transition-opacity"
                    [class.opacity-100]="copiedViewId() === view.id"
                    [class.opacity-0]="copiedViewId() !== view.id"
                    [class.group-hover:opacity-100]="copiedViewId() !== view.id"
                    [class.text-emerald-400]="copiedViewId() === view.id"
                    [class.text-zinc-400]="copiedViewId() !== view.id"
                    [class.hover:text-white]="copiedViewId() !== view.id"
                    [title]="copiedViewId() === view.id ? 'Copied!' : 'Copy view link'"
                  >
                    @if (copiedViewId() === view.id) {
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    } @else {
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    }
                  </button>
                  <button
                    type="button"
                    (click)="confirmDeleteView(view, $event)"
                    class="shrink-0 opacity-0 group-hover:opacity-100 text-zinc-400 hover:text-red-400 p-1.5 rounded-md cursor-pointer transition-opacity"
                    title="Delete view"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6h16Z" />
                    </svg>
                  </button>
                }
              </div>
            } @empty {
              <div class="px-3 py-2 text-xs text-zinc-500">No saved views yet</div>
            }
          </div>
        }
      </div>

      @if (periodOptions().length > 0) {
        <div class="flex flex-col gap-2">
          <span class="text-[11px] font-bold tracking-wider text-text-dimmed uppercase">PERIOD</span>
          <div class="flex flex-wrap gap-2">
            @for (option of periodOptions(); track option.id) {
              <button
                type="button"
                (click)="selectPeriod(option, $event)"
                class="transition-colors cursor-pointer select-none"
                [ngClass]="
                  activePeriodId() === option.id
                    ? 'bg-white text-zinc-950 font-semibold px-3 py-1 rounded-full text-xs hover:bg-zinc-200 shadow-xs'
                    : 'bg-zinc-800/80 text-zinc-400 font-medium px-3 py-1 rounded-full text-xs hover:text-white hover:bg-zinc-700 border border-zinc-700/60'
                "
              >
                {{ option.label }}
              </button>
            }
          </div>
        </div>
      }

      <!-- Section 2: KPI CARDS (shown if KPI cards exist) -->
      @if (kpiCards().length > 0) {
        <div class="flex flex-col gap-2">
          <span class="text-[11px] font-bold tracking-wider text-text-dimmed uppercase">KPI CARDS</span>
          <div class="flex flex-wrap gap-2">
            @for (kpi of kpiCards(); track kpi.id) {
              <button
                type="button"
                (click)="toggleKpi(kpi, $event)"
                class="transition-colors cursor-pointer select-none"
                [ngClass]="
                  kpi.visible
                    ? 'bg-white text-zinc-950 font-semibold px-3 py-1 rounded-full text-xs hover:bg-zinc-200 shadow-xs'
                    : 'bg-zinc-800/80 text-zinc-400 font-medium px-3 py-1 rounded-full text-xs hover:text-white hover:bg-zinc-700 border border-zinc-700/60'
                "
              >
                {{ kpi.label }}
              </button>
            }
          </div>
        </div>
      }

      <!-- Section 3: COLUMNS -->
      <div class="flex flex-col gap-1.5">
        <span class="text-[11px] font-bold tracking-wider text-text-dimmed uppercase">COLUMNS</span>
        <div class="flex flex-col gap-1 max-h-[340px] overflow-y-auto pr-1 custom-scrollbar">
          @for (col of columns(); track col.id; let i = $index) {
            <div
              class="flex items-center justify-between py-1.5 px-2 rounded-lg transition-colors group select-none border-t-2 border-b-2 border-transparent"
              [class.cursor-grab]="!col.immutable"
              [class.active:cursor-grabbing]="!col.immutable"
              [class.hover:bg-zinc-800/40]="!col.immutable"
              [class.border-t-indigo-500]="!col.immutable && dropTargetIndex() === i && draggedIndex() !== i && dropPosition() === 'above'"
              [class.border-b-indigo-500]="!col.immutable && dropTargetIndex() === i && draggedIndex() !== i && dropPosition() === 'below'"
              [class.opacity-50]="draggedIndex() === i"
              [attr.draggable]="!col.immutable ? 'true' : null"
              (dragstart)="!col.immutable ? onDragStart(i, $event) : $event.preventDefault()"
              (dragover)="!col.immutable ? onDragOver(i, $event) : null"
              (dragleave)="!col.immutable ? onDragLeave(i, $event) : null"
              (drop)="!col.immutable ? onDrop(i, $event) : null"
              (dragend)="!col.immutable ? onDragEnd() : null"
            >
              <!-- Drag Handle & Column Name -->
              <div class="flex items-center gap-2.5 min-w-0 flex-1 mr-2 pointer-events-none">
                @if (!col.immutable) {
                  <svg
                    class="w-3.5 h-3.5 text-zinc-500 group-hover:text-zinc-300 shrink-0"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <circle cx="9" cy="6" r="1.5" />
                    <circle cx="15" cy="6" r="1.5" />
                    <circle cx="9" cy="12" r="1.5" />
                    <circle cx="15" cy="12" r="1.5" />
                    <circle cx="9" cy="18" r="1.5" />
                    <circle cx="15" cy="18" r="1.5" />
                  </svg>
                } @else {
                  <div class="w-3.5 h-3.5 shrink-0" aria-hidden="true"></div>
                }
                <span class="text-sm font-medium text-text-primary truncate">{{ col.header }}</span>
              </div>

              <!-- Toggle Switch -->
              @if (col.immutable) {
                <!-- Immutable (Always On, Disabled, Gray) -->
                <div
                  class="w-9 h-5 rounded-full relative bg-zinc-600/70 cursor-not-allowed shrink-0 transition-colors"
                  [class.pointer-events-none]="draggedIndex() !== null"
                  title="Mandatory column"
                >
                  <div
                    class="w-3.5 h-3.5 bg-white rounded-full absolute top-[3px] left-[3px] translate-x-4 shadow-xs"
                  ></div>
                </div>
              } @else {
                <!-- Mutable Switch -->
                <button
                  type="button"
                  (click)="toggleColumn(col, $event)"
                  class="w-9 h-5 rounded-full relative shrink-0 transition-colors cursor-pointer"
                  [class.pointer-events-none]="draggedIndex() !== null"
                  [class.bg-indigo-600]="col.visible !== false"
                  [class.bg-zinc-700]="col.visible === false"
                  [title]="col.visible !== false ? 'Hide column' : 'Show column'"
                >
                  <div
                    class="w-3.5 h-3.5 bg-white rounded-full absolute top-[3px] left-[3px] transition-transform duration-150 shadow-xs"
                    [class.translate-x-4]="col.visible !== false"
                    [class.translate-x-0]="col.visible === false"
                  ></div>
                </button>
              }
            </div>
          }
        </div>
      </div>

      @if (isDirty()) {
        <div class="flex items-center justify-between gap-2 pt-2 border-t border-zinc-800">
          <button
            type="button"
            (click)="onResetClick()"
            class="text-xs font-semibold text-zinc-400 hover:text-white cursor-pointer transition-colors"
          >
            Reset
          </button>

          @if (isNamingNewView()) {
            <div class="flex items-center gap-1.5">
              <input
                #nameInput
                type="text"
                [value]="nameDraft()"
                (input)="onNameInput($any($event.target).value)"
                (keydown.enter)="confirmCreateName()"
                (keydown.escape)="cancelCreateName(); $event.stopPropagation()"
                class="h-[26px] px-2 text-xs bg-zinc-950 border rounded-md text-text-primary w-32 outline-none"
                [class.border-zinc-700]="!nameError()"
                [class.focus:border-indigo-500]="!nameError()"
                [class.border-red-500]="!!nameError()"
              />
              <button
                type="button"
                (click)="confirmCreateName()"
                class="h-[26px] px-3 rounded-md bg-white text-zinc-950 text-xs font-bold hover:bg-zinc-200 cursor-pointer transition-colors"
              >
                Done
              </button>
            </div>
          } @else {
            <button
              type="button"
              (click)="onSaveClick()"
              class="h-[26px] px-3 rounded-md bg-white text-zinc-950 text-xs font-bold hover:bg-zinc-200 cursor-pointer transition-colors"
            >
              {{ activeView() ? 'Update view' : 'Save view' }}
            </button>
          }
        </div>
        @if (isNamingNewView() && nameError()) {
          <p class="-mt-2 text-right text-[11px] text-red-400">{{ nameError() }}</p>
        }
      }
    </div>
  }
</div>
